import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LeftNav from "../LeftNav";
import { authorizedHttpGet, authorizedHttpPost } from "../../api/httpCalls";
import Loader from "../Loader";
import EventForm from "./event/EventForm";
import EventList from "./event/EventList";
import {
    buildEventFormData,
    createEventFormStateFromEvent,
    createInitialEventFormState,
    deriveOfflineEventPricingState,
    readFileAsDataUrl,
    resolveEventImageUrls,
    validateEventFormCity,
    validateEventFormImages
} from "./event/eventFormUtils";
import { pageContentStyle } from "./event/eventStyles";

/**
 * CMS tab for managing app events: lists events, creates new ones, and edits existing records.
 * Created as the top-level container so routing, auth, app context (LeftNav), loading state, and
 * list vs. form view stay in one place while the form and list UI live in smaller components.
 */
const Event = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const authToken = localStorage.getItem('authToken');
    const isOtpVerified = localStorage.getItem('verificationPassed');
    if (!authToken || !isOtpVerified) {
        sessionStorage.setItem('redirectUrl', location.pathname)
        navigate('/login')
    }

    const [appName, setAppName] = useState(localStorage.getItem('appName'));
    const [formValues, setFormValues] = useState(createInitialEventFormState);
    const [viewMode, setViewMode] = useState('list');
    const [events, setEvents] = useState([]);
    const [cities, setCities] = useState([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isEdit = viewMode === 'edit';
    const isDisplay = viewMode === 'list';

    /**
     * Restores the event form to a sensible baseline without leaving the form screen.
     * Exists so users can discard in-progress edits in create/edit mode: in edit, reloads from
     * the server-backed list row; otherwise clears to the empty template.
     */
    const resetForm = () => {
        if (isEdit) {
            const currentEvent = events.find((event) => event.event_id === formValues.eventId);
            if (currentEvent) {
                setFormValues(createEventFormStateFromEvent(currentEvent));
                return;
            }
        }

        setFormValues(createInitialEventFormState());
    };

    /**
     * Switches to edit mode and hydrates the form from a stored event row.
     * Centralizes the transition from list → edit so EventList only passes an id and this tab
     * owns form state and view mode.
     */
    const handleEdit = (eventId) => {
        const event = events.find((event) => event.event_id === eventId);
        if (!event) {
            return;
        }

        setViewMode('edit');
        setFormValues(createEventFormStateFromEvent(event));
    };

    /**
     * Opens the form for a new event with a fresh initial state.
     * Separated from edit so the list can offer “create” without duplicating form initialization.
     */
    const handleNew = () => {
        setViewMode('create');
        setFormValues(createInitialEventFormState());
    };

    /**
     * Keeps `appName` in sync when the user picks a different app in the left navigation.
     * Required because events are scoped per app; downstream API calls read `appName` from state.
     */
    const handleLeftNavAppChange = (name) => {
        setAppName(name);
    };

    /**
     * Generic controlled-field updater for text, dates, and other scalar form keys.
     * One handler avoids a long prop list of setters and keeps EventForm dumb about state shape.
     */
    const handleFieldChange = (field, value) => {
        const pricingFields = new Set(['eventType', 'maleDiscount', 'femaleDiscount']);

        setFormValues((previousValues) => {
            const nextValues = {
                ...previousValues,
                [field]: value
            };

            if (!pricingFields.has(field)) {
                return nextValues;
            }

            return {
                ...nextValues,
                ...deriveOfflineEventPricingState({
                    appName,
                    eventType: nextValues.eventType,
                    maleDiscount: nextValues.maleDiscount,
                    femaleDiscount: nextValues.femaleDiscount,
                    maleAmount: nextValues.maleAmount,
                    femaleAmount: nextValues.femaleAmount
                })
            };
        });
    };

    /**
     * Commits a non-empty draft string into a list field (e.g. bullet points) and clears the draft.
     * Parameterized by field names so the same logic serves multiple list+draft pairs without copy-paste.
     */
    const handleAddPoint = (pointsField, draftField) => {
        setFormValues((previousValues) => {
            const draftValue = previousValues[draftField].trim();
            if (!draftValue) {
                return previousValues;
            }

            return {
                ...previousValues,
                [pointsField]: [...previousValues[pointsField], draftValue],
                [draftField]: ''
            };
        });
    };

    /**
     * Removes one entry from a string array field by index (immutable update).
     * Mirrors `handleAddPoint` so list UIs can add/remove rows without reaching into form state directly.
     */
    const handleRemovePoint = (pointsField, index) => {
        setFormValues((previousValues) => ({
            ...previousValues,
            [pointsField]: previousValues[pointsField].filter((_, itemIndex) => itemIndex !== index)
        }));
    };

    /**
     * Reads a chosen file as a data URL and stores preview + pending-upload flags for app or web image.
     * Async file IO and branching by `imageType` stay here so EventImageField stays presentational.
     */
    const handleImageChange = async (imageType, file) => {
        if (!file) {
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);

            setFormValues((previousValues) => ({
                ...previousValues,
                ...(imageType === 'app'
                    ? {
                        appImagePreview: dataUrl,
                        pendingAppImageUpload: dataUrl,
                        hasAppImageChanged: true
                    }
                    : {
                        webImagePreview: dataUrl,
                        pendingWebImageUpload: dataUrl,
                        hasWebImageChanged: true
                    })
            }));
        } catch (error) {
            alert('Failed to read image');
        }
    };

    /**
     * Toggles or completes an event’s lifecycle state via the API, then refreshes the list on success.
     * Lives in this tab (not EventList) to share loading/error handling and `appName` with other calls;
     * maps UI “active” clicks to the backend’s pending/active convention except when completing.
     */
    const setEventState = async (eventId, eventState) => {
        setIsLoading(true);
        if (eventState === 'completed') {
            if (!window.confirm('Are you sure you want to mark this event as completed?')) {
                setIsLoading(false);
                return;
            }
        }
        if (eventState !== 'completed') {
            eventState = eventState === 'active'
                ? (appName.toLowerCase() ===  'aisle' ? 'pending' : 'not_active')
                : 'active';
        }
        const params = {
            event_state: eventState,
            event_id: eventId,
            app_name: appName
        };
        try {
            const response = await authorizedHttpPost("events/update_event_state", params);
            if (response.status && response.data?.status) {
                await fetchEvents(false);
            } else {
                alert(response.data?.message || 'Error updating event state');
            }
        } catch (error) {
            alert('Error updating event state');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Validates images, uploads/resolves URLs, builds the payload, posts create/update, then returns to list.
     * Orchestrates the full save pipeline so the form only triggers one callback and errors stay centralized.
     */
    const saveChanges = async () => {
        if (!validateEventFormCity(formValues)) {
            return;
        }

        if (!validateEventFormImages(formValues, isEdit)) {
            return;
        }

        setIsLoading(true);

        try {
            const imageUrls = await resolveEventImageUrls(formValues, appName);
            if (!imageUrls) {
                return;
            }

            setFormValues((previousValues) => ({
                ...previousValues,
                appImageUrl: imageUrls.appImageUrl,
                webImageUrl: imageUrls.webImageUrl,
                pendingAppImageUpload: '',
                pendingWebImageUpload: '',
                hasAppImageChanged: false,
                hasWebImageChanged: false
            }));

            const formData = buildEventFormData({
                formValues,
                appName,
                imageUrls,
                isEdit
            });

            const response = await authorizedHttpPost("events/create_event", formData);
            if (!response.status || response.data?.status === false) {
                alert(response.data?.message || 'Failed to save event');
                return;
            }

            setFormValues(createInitialEventFormState());
            setViewMode('list');
            await fetchEvents(false);
        } catch (error) {
            alert('Error saving event');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Loads all events for the current `appName` from the API and updates local list state.
     * `shouldSetLoading` allows silent refreshes after mutations so the global loader does not flicker.
     */
    const fetchEvents = async (shouldSetLoading = true) => {
        if (shouldSetLoading) {
            setIsLoading(true);
        }

        try {
            const response = await authorizedHttpGet("events/all", { app_name: appName });
            if (response.status) {
                setEvents(Array.isArray(response.data) ? response.data : []);
            } else {
                console.log(response)
                alert(response.data?.message || 'Error fetching events');
            }
        } catch (error) {
            console.log(error)
            alert('Error fetching events');
        } finally {
            if (shouldSetLoading) {
                setIsLoading(false);
            }
        }
    };

    /**
     * Loads offline event cities for the selected app and normalizes the API shape for the form dropdown.
     * Keeps the select disabled until data arrives so the user cannot save with a phantom default.
     */
    const fetchCities = async () => {
        setIsCitiesLoading(true);

        try {
            const response = await authorizedHttpGet("events/cities");
            if (!response.status || response.data?.success === false) {
                setCities([]);
                alert(response.data?.message || 'Error fetching cities');
                return;
            }

            const cityList = Array.isArray(response.data?.data) ? response.data.data : [];
            setCities(cityList);
        } catch (error) {
            console.log(error)
            setCities([]);
            alert('Error fetching cities');
        } finally {
            setIsCitiesLoading(false);
        }
    };

    /**
     * Abandons the form: clears fields and returns to the list view without calling the server.
     * Distinct from `resetForm`, which stays on the form and only resets field values.
     */
    const handleCancel = () => {
        setFormValues(createInitialEventFormState());
        setViewMode('list');
    };

    /**
     * When the selected app changes, reset form/view to a safe default and reload events for that app.
     * Prevents showing another app’s draft or list after switching context in LeftNav.
     */
    useEffect(() => {
        console.log('app', appName)
        setFormValues(createInitialEventFormState());
        setViewMode('list');
        fetchEvents();
        fetchCities();
    }, [appName]);


    return (
        <div>
            <LeftNav handleLeftNavAppChange={handleLeftNavAppChange} tabName={'Event'} />
            <div style={pageContentStyle}>
                {isLoading ? <Loader /> : null}
                {isDisplay ? (
                    <EventList
                        events={events}
                        onCreate={handleNew}
                        onEdit={handleEdit}
                        onSetEventState={setEventState}
                    />
                ) : (
                    <EventForm
                        appName={appName}
                        cities={cities}
                        formValues={formValues}
                        isCitiesLoading={isCitiesLoading}
                        isEdit={isEdit}
                        onFieldChange={handleFieldChange}
                        onImageChange={handleImageChange}
                        onAddPoint={handleAddPoint}
                        onRemovePoint={handleRemovePoint}
                        onSubmit={saveChanges}
                        onReset={resetForm}
                        onCancel={handleCancel}
                    />
                )}
            </div>
        </div >
    );
};

export default Event;
